import React, { useState, useRef } from 'react';
import { HazardType, Alert, Language } from '../types';
import { getCurrentPosition } from '../services/locationService';
import { EMERGENCY_NUMBERS } from '../constants';
import { ShieldAlert, Phone, Radio, Copy, MapPin } from 'lucide-react';

interface ReportHazardProps {
    onClose: () => void;
    onReport?: (hazard: Alert) => void;
    onActivateBeacon: (type: HazardType) => void;
    lang: Language;
}

const ReportHazard: React.FC<ReportHazardProps> = ({ onClose, onReport, onActivateBeacon, lang }) => {
    const [step, setStep] = useState<'hold' | 'select' | 'actions'>('hold');
    const [progress, setProgress] = useState(0);
    const [isHolding, setIsHolding] = useState(false);
    const [selectedHazard, setSelectedHazard] = useState<HazardType | null>(null);

    const holdIntervalRef = useRef<any>(null);
    const [itemsVisible, setItemsVisible] = useState(false);

    // Emergency Number Logic (Simplified detection)
    const [emergencyNumber, setEmergencyNumber] = useState(EMERGENCY_NUMBERS.DEFAULT.general);

    // Available Hazards
    const hazards = [
        { type: HazardType.FIRE, icon: '🔥', color: '#ff4444', label: lang === 'ar' ? 'حريق' : 'FIRE' },
        { type: HazardType.MEDICAL, icon: '🚑', color: '#00ccff', label: lang === 'ar' ? 'إسعاف' : 'MEDIC' },
        { type: HazardType.POLICE, icon: '👮', color: '#3366ff', label: lang === 'ar' ? 'شرطة' : 'POLICE' },
        { type: HazardType.CRASH, icon: '💥', color: '#ffcc00', label: lang === 'ar' ? 'حادث' : 'CRASH' },
        { type: HazardType.FLOOD, icon: '🌊', color: '#00ffaa', label: lang === 'ar' ? 'فيضان' : 'FLOOD' },
        { type: HazardType.CIVIL, icon: '📢', color: '#cc33ff', label: lang === 'ar' ? 'شغب' : 'CIVIL' },
    ];

    // Hold Logic
    const startHold = () => {
        setIsHolding(true);
        if (step === 'hold') {
            const startTime = Date.now();
            const duration = 1500; // 1.5s for faster interaction

            holdIntervalRef.current = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const p = Math.min((elapsed / duration) * 100, 100);
                setProgress(p);

                if (p >= 100) {
                    clearInterval(holdIntervalRef.current);
                    setStep('select');
                    if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
                    setTimeout(() => setItemsVisible(true), 100);
                }
            }, 16);
        }
    };

    const endHold = () => {
        setIsHolding(false);
        clearInterval(holdIntervalRef.current);
        if (step === 'hold') {
            setProgress(0);
        }
    };

    const getPosition = (index: number, total: number, radius: number) => {
        const angle = (index * (360 / total)) - 90;
        const rad = angle * (Math.PI / 180);
        const tx = Math.cos(rad) * radius;
        const ty = Math.sin(rad) * radius;
        return { x: tx, y: ty };
    };

    const handleSelect = async (type: HazardType) => {
        if (navigator.vibrate) navigator.vibrate(200);
        setSelectedHazard(type);

        // 1. Activate System Logic
        onActivateBeacon(type);

        const location = await getCurrentPosition();
        const newHazard: Alert = {
            id: `sos-${Date.now()}`,
            type: type,
            severity: 'critical',
            location: 'SOS BEACON',
            coordinates: location,
            timestamp: 'Just now',
            distance: '0km',
            isOfficial: false,
            verifiedCount: 0,
            description: 'Emergency Beacon Activated. User requests immediate assistance.'
        };
        if (onReport) onReport(newHazard);

        // 2. Determine Emergency Number
        let num = EMERGENCY_NUMBERS.DEFAULT.general;
        if (type === HazardType.POLICE || type === HazardType.CIVIL) num = EMERGENCY_NUMBERS.DEFAULT.police;
        if (type === HazardType.FIRE) num = EMERGENCY_NUMBERS.DEFAULT.fire;
        if (type === HazardType.MEDICAL) num = EMERGENCY_NUMBERS.DEFAULT.ambulance;
        setEmergencyNumber(num);

        // 3. Move to Action Phase
        setStep('actions');
    };

    // --- ACTIONS SCREEN RENDERER ---
    const renderActions = () => {
        const hData = hazards.find(h => h.type === selectedHazard);
        return (
            <div className="flex flex-col items-center w-full max-w-sm animate-fade-in px-4">

                {/* Confirmed Header */}
                <div className="text-center mb-8 relative">
                    <div className="absolute inset-0 bg-red-500/20 blur-3xl rounded-full" />
                    <div className="relative z-10 w-24 h-24 rounded-2xl border-2 border-white/20 flex items-center justify-center mx-auto mb-4 bg-red-500/10 animate-pulse shadow-[0_0_30px_rgba(239,68,68,0.4)]">
                        <span className="text-5xl drop-shadow-lg">{hData?.icon}</span>
                    </div>
                    <h2 className="text-3xl font-display font-black text-white tracking-widest uppercase italic">
                        {lang === 'ar' ? 'تم تفعيل التنبيه' : 'BEACON ACTIVE'}
                    </h2>
                    <p className="text-xs text-red-500 font-mono font-bold mt-1 tracking-[0.2em] uppercase">
                        {lang === 'ar' ? 'جاري مشاركة الموقع' : 'Broadcasting Live Location'}
                    </p>
                </div>

                {/* Action Grid */}
                <div className="w-full space-y-3">

                    {/* 1. Call Emergency */}
                    <a
                        href={`tel:${emergencyNumber}`}
                        className="flex items-center justify-between bg-white text-red-600 p-5 rounded-xl shadow-[0_0_20px_rgba(255,255,255,0.2)] border-2 border-transparent hover:border-red-500 hover:scale-[1.02] transition-all group"
                    >
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <Phone className="w-6 h-6 text-red-600 group-hover:animate-shake" />
                            </div>
                            <div className="text-left">
                                <div className="text-xs font-bold tracking-widest opacity-80 uppercase">{lang === 'ar' ? 'اتصل بالطوارئ' : 'Call Emergency'}</div>
                                <div className="text-3xl font-black font-mono leading-none tracking-tighter">{emergencyNumber}</div>
                            </div>
                        </div>
                        <div className="p-2 bg-red-600 text-white rounded-full">
                            <Phone className="w-5 h-5 fill-current" />
                        </div>
                    </a>

                    {/* 2. Notify Squad */}
                    <button
                        onClick={() => {
                            // In a real app, this might trigger a specific push notification or SMS intent
                            alert(lang === 'ar' ? "تم إرسال تنبيه للفريق" : "Priority Alert sent to all Squad members.");
                        }}
                        className="w-full flex items-center justify-between bg-blue-600/10 border border-blue-500/50 text-white p-4 rounded-xl hover:bg-blue-600/20 hover:border-blue-400 transition-all group"
                    >
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center border border-blue-500/30">
                                <Radio className="w-5 h-5 text-blue-400 group-hover:animate-ping" />
                            </div>
                            <div className="text-left">
                                <div className="text-xs font-bold tracking-widest text-blue-400 uppercase">{lang === 'ar' ? 'نداء للفريق' : 'Alert Squad'}</div>
                                <div className="text-xs text-gray-400 font-mono">{lang === 'ar' ? 'إرسال تنبيه عاجل' : 'Send Priority Ping'}</div>
                            </div>
                        </div>
                    </button>

                    {/* 3. Share Location Link (Copy) */}
                    <button
                        onClick={() => {
                            navigator.clipboard.writeText("https://lanaja.app/track/usr-12345");
                            alert("Location Link Copied");
                        }}
                        className="w-full flex items-center justify-between bg-white/5 border border-white/10 text-white p-4 rounded-xl hover:bg-white/10 transition-all"
                    >
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                                <Copy className="w-5 h-5" />
                            </div>
                            <div className="text-left">
                                <div className="text-xs font-bold tracking-widest uppercase">{lang === 'ar' ? 'نسخ الرابط' : 'Copy Coordinates'}</div>
                                <div className="text-xs text-gray-400 font-mono">{lang === 'ar' ? 'مشاركة الإحداثيات' : 'Share secure link manually'}</div>
                            </div>
                        </div>
                    </button>

                </div>

                {/* Close / Minimize */}
                <button
                    onClick={onClose}
                    className="mt-8 text-[10px] text-gray-500 hover:text-white underline font-mono tracking-widest uppercase"
                >
                    {lang === 'ar' ? 'إخفاء (يبقى التتبع نشطاً)' : 'Minimize (Beacon Remains Active)'}
                </button>

            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-[100] bg-[#020817]/95 backdrop-blur-xl flex flex-col items-center justify-center p-4 animate-fade-in overflow-hidden font-sans">

            {/* Background Grid */}
            <div className={`absolute inset-0 bg-[linear-gradient(rgba(239,68,68,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(239,68,68,0.05)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none transition-all duration-500 ${step === 'actions' ? 'opacity-20 animate-pulse' : 'opacity-10'}`}></div>

            {/* Background Pulse for Actions */}
            {step === 'actions' && (
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/40 via-transparent to-transparent animate-pulse pointer-events-none"></div>
            )}

            {/* Header Info (Hidden in Actions Phase) */}
            {step !== 'actions' && (
                <div className="absolute top-10 w-full text-center pointer-events-none z-20">
                    <h2 className={`text-3xl font-display font-black tracking-[0.2em] italic uppercase ${step === 'select' ? 'text-red-500 animate-pulse' : 'text-gray-400'}`}>
                        {step === 'select' ? (lang === 'ar' ? 'تأكيد الخطر' : 'Confirm Threat') : (lang === 'ar' ? 'نظام الطوارئ' : 'Emergency Uplink')}
                    </h2>
                    <p className="text-xs font-mono text-gray-500 mt-2 uppercase tracking-widest">
                        {step === 'select' ? (lang === 'ar' ? 'حدد نوع الخطر لتفعيل التتبع' : 'Select Hazard Class') : (lang === 'ar' ? 'اضغط مطولاً للتفعيل' : 'Hold to Initialize')}
                    </p>
                </div>
            )}

            {/* Main Interactive Area */}
            {step !== 'actions' ? (
                <>
                    <div className="relative w-80 h-80 flex items-center justify-center">

                        {/* Central Button */}
                        <button
                            onMouseDown={startHold}
                            onMouseUp={endHold}
                            onMouseLeave={endHold}
                            onTouchStart={startHold}
                            onTouchEnd={endHold}
                            className={`relative w-40 h-40 rounded-full flex items-center justify-center transition-all duration-300 z-30 group ${step === 'select'
                                    ? 'bg-black border-2 border-white/10 scale-75 opacity-50'
                                    : 'bg-gradient-to-br from-red-600 to-red-900 border border-red-500/50 shadow-[0_0_60px_rgba(220,38,38,0.4)] active:scale-95'
                                }`}
                        >
                            {/* Ring Animation (When Idle) */}
                            {step === 'hold' && !isHolding && (
                                <div className="absolute inset-[-20px] rounded-full border border-red-500/20 animate-ping" />
                            )}

                            {/* Progress Ring (SVG) */}
                            {step === 'hold' && (
                                <svg className="absolute inset-[-15px] w-[calc(100%+30px)] h-[calc(100%+30px)] rotate-[-90deg]" viewBox="0 0 100 100">
                                    {/* Track */}
                                    <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="2" />
                                    {/* Indicator */}
                                    <circle
                                        cx="50" cy="50" r="46" fill="none" stroke="#ef4444" strokeWidth="4"
                                        strokeDasharray="289"
                                        strokeDashoffset={289 - (289 * progress) / 100}
                                        strokeLinecap="round"
                                        className="transition-all duration-75"
                                        style={{ filter: 'drop-shadow(0 0 10px #ef4444)' }}
                                    />
                                </svg>
                            )}

                            <div className={`flex flex-col items-center ${isHolding ? 'scale-110' : ''} transition-transform duration-200`}>
                                <ShieldAlert className={`w-12 h-12 ${step === 'select' ? 'text-gray-500' : 'text-white drop-shadow-md'}`} />
                                {step === 'hold' && <span className="text-xs font-black tracking-[0.3em] mt-2 text-white/80">SOS</span>}
                            </div>
                        </button>

                        {/* Radial Items */}
                        {step === 'select' && hazards.map((h, i) => {
                            const pos = getPosition(i, hazards.length, 140);
                            return (
                                <button
                                    key={h.type}
                                    onClick={() => handleSelect(h.type)}
                                    style={{
                                        transform: itemsVisible
                                            ? `translate(${pos.x}px, ${pos.y}px) scale(1)`
                                            : `translate(0px, 0px) scale(0)`,
                                        borderColor: h.color,
                                        boxShadow: `0 0 20px ${h.color}40`,
                                        transitionDelay: `${i * 50}ms`
                                    }}
                                    className="absolute w-20 h-20 rounded-2xl bg-black/80 border-2 backdrop-blur-md flex flex-col items-center justify-center transition-all duration-500 z-20 hover:scale-110 active:scale-95 group"
                                >
                                    <span className="text-2xl mb-1 group-hover:animate-bounce">{h.icon}</span>
                                    <span className="text-[9px] font-bold uppercase tracking-wide text-white">{h.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Cancel Button */}
                    <button
                        onClick={onClose}
                        className="absolute bottom-12 text-gray-500 hover:text-white text-xs font-mono tracking-widest border border-white/10 px-8 py-3 rounded-full hover:bg-white/5 transition uppercase"
                    >
                        {lang === 'ar' ? 'إلغاء' : 'Cancel Sequence'}
                    </button>
                </>
            ) : (
                // ACTIONS SCREEN
                renderActions()
            )}

        </div>
    );
};

export default ReportHazard;
